import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Banknote,
  Workflow,
  Megaphone,
  Users,
  Building2,
  RefreshCw,
  XCircle,
  ExternalLink,
  Filter,
  Sun,
  Moon,
  Monitor,
  ScrollText,
  Languages,
  CalendarDays,
  Briefcase,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useDashboard } from "@/hooks/useDashboard";
import { useTheme } from "@/hooks/useTheme";

const PAGES: { to: string; key: string; icon: typeof LayoutDashboard; hint: string }[] = [
  { to: "/", key: "nav.overview", icon: LayoutDashboard, hint: "G O" },
  { to: "/weekly-plan", key: "nav.weekly_plan", icon: CalendarDays, hint: "G W" },
  { to: "/wip", key: "nav.wip", icon: Briefcase, hint: "G I" },
  { to: "/finance", key: "nav.finance", icon: Banknote, hint: "G F" },
  { to: "/operations", key: "nav.operations", icon: Workflow, hint: "G P" },
  { to: "/marketing", key: "nav.marketing", icon: Megaphone, hint: "G M" },
  { to: "/clients", key: "nav.clients", icon: Users, hint: "G C" },
  { to: "/audit", key: "nav.audit", icon: ScrollText, hint: "G A" },
];

/**
 * Cmd Palette — ⌘K / Ctrl+K
 * Navigation + tenant switcher + utility actions in one keystroke surface.
 * Per ADR D2 (cmdk).
 */
export default function CommandPalette() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, selectedTenant, setSelectedTenant, refetch } = useDashboard();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();

  // ⌘K / Ctrl+K toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const run = (fn: () => void) => {
    fn();
    setOpen(false);
  };

  const clients = data?.clients ?? [];
  const currentLang = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 max-w-xl gap-0 [&>button[aria-label]]:hidden [&>button:last-child]:hidden">
        <DialogTitle className="sr-only">{t("palette.title")}</DialogTitle>

        <Command label={t("palette.title")}>
          <CommandInput placeholder={t("palette.placeholder")} />
          <CommandList>
            <CommandEmpty>{t("common.no_results")}</CommandEmpty>

            <CommandGroup heading={t("palette.groups.navigation")}>
              {PAGES.map((p) => (
                <CommandItem
                  key={p.to}
                  value={`page ${t(p.key)} ${p.to}`}
                  onSelect={() => run(() => navigate(p.to))}
                >
                  <p.icon className="h-4 w-4 text-slate-400" />
                  <span>{t(p.key)}</span>
                  <CommandShortcut>{p.hint}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>

            {clients.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={t("palette.groups.tenants", { count: clients.length })}>
                  {clients.map((c) => {
                    const isSelected = selectedTenant === c.slug;
                    return (
                      <CommandItem
                        key={c.slug}
                        value={`tenant ${c.slug} ${c.display_name} ${c.vertical}`}
                        onSelect={() => run(() => setSelectedTenant(c.slug))}
                      >
                        <Building2 className="h-4 w-4 text-slate-400" />
                        <span className="truncate">{c.display_name}</span>
                        <span className="text-[10px] text-slate-500">
                          · {c.vertical}
                        </span>
                        {isSelected && (
                          <span className="ml-auto text-[10px] text-accent">
                            {t("language.current")}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}

            <CommandSeparator />

            <CommandGroup heading={t("palette.groups.actions")}>
              {selectedTenant && (
                <CommandItem
                  value="action clear filter"
                  onSelect={() => run(() => setSelectedTenant(null))}
                >
                  <XCircle className="h-4 w-4 text-slate-400" />
                  <span>{t("palette.actions.clear_filter")}</span>
                  <CommandShortcut>esc</CommandShortcut>
                </CommandItem>
              )}
              <CommandItem
                value="action refresh sync"
                onSelect={() =>
                  run(() => {
                    qc.invalidateQueries({ queryKey: ["dashboard"] });
                    refetch();
                  })
                }
              >
                <RefreshCw className="h-4 w-4 text-slate-400" />
                <span>{t("palette.actions.refresh")}</span>
                <CommandShortcut>R</CommandShortcut>
              </CommandItem>
              <CommandItem
                value="action open ops legacy dashboard"
                onSelect={() =>
                  run(() => window.open("https://ops.growtify.app", "_blank"))
                }
              >
                <ExternalLink className="h-4 w-4 text-slate-400" />
                <span>{t("palette.actions.open_legacy")}</span>
              </CommandItem>
              <CommandItem value="action filter help" disabled>
                <Filter className="h-4 w-4 text-slate-400" />
                <span>{t("palette.actions.filter_help")}</span>
              </CommandItem>
              <CommandItem value="action keyboard navigation help" disabled>
                <Filter className="h-4 w-4 text-slate-400" />
                <span className="text-[11px] font-mono">
                  {t("palette.actions.kbd_hint")}
                </span>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading={t("palette.groups.theme")}>
              <CommandItem
                value="theme light"
                onSelect={() => run(() => setThemeMode("light"))}
              >
                <Sun className="h-4 w-4 text-slate-400" />
                <span>{t("theme.light")}</span>
                {themeMode === "light" && (
                  <span className="ml-auto text-[10px] text-accent">
                    {t("theme.current")}
                  </span>
                )}
              </CommandItem>
              <CommandItem
                value="theme dark"
                onSelect={() => run(() => setThemeMode("dark"))}
              >
                <Moon className="h-4 w-4 text-slate-400" />
                <span>{t("theme.dark")}</span>
                {themeMode === "dark" && (
                  <span className="ml-auto text-[10px] text-accent">
                    {t("theme.current")}
                  </span>
                )}
              </CommandItem>
              <CommandItem
                value="theme system auto"
                onSelect={() => run(() => setThemeMode("system"))}
              >
                <Monitor className="h-4 w-4 text-slate-400" />
                <span>{t("theme.system")}</span>
                {themeMode === "system" && (
                  <span className="ml-auto text-[10px] text-accent">
                    {t("theme.current")}
                  </span>
                )}
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading={t("palette.groups.language")}>
              <CommandItem
                value="language en english"
                onSelect={() => run(() => i18n.changeLanguage("en"))}
              >
                <Languages className="h-4 w-4 text-slate-400" />
                <span>{t("language.en")}</span>
                {currentLang === "en" && (
                  <span className="ml-auto text-[10px] text-accent">
                    {t("language.current")}
                  </span>
                )}
              </CommandItem>
              <CommandItem
                value="language tr turkce turkish"
                onSelect={() => run(() => i18n.changeLanguage("tr"))}
              >
                <Languages className="h-4 w-4 text-slate-400" />
                <span>{t("language.tr")}</span>
                {currentLang === "tr" && (
                  <span className="ml-auto text-[10px] text-accent">
                    {t("language.current")}
                  </span>
                )}
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
